'use client';

/**
 * ObeyaModeToggleSection — Projects | Simulations switcher.
 *
 * Owns no state itself — reads/writes the active sub-mode via
 * `useObeyaExplorer()`. Toggling to "simulations" remounts the
 * narrative-search provider underneath; toggling back unmounts it
 * (which is what the legacy panel did via local React state).
 *
 * Labels come from the same React Query cache the legacy panel used
 * (`useObeyaProjects` → `data.labels`) so EN/ZH translations remain
 * consistent with the projects list and the surrounding chrome.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.OBEYA_MODE_TOGGLE
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer / Obeya
 * @layer: presentation
 */

import { Bot, FolderKanban } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useObeyaProjects } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useObeyaExplorer, type ObeyaSubMode } from '../providers/obeya-explorer-provider';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';

interface ObeyaModeToggleSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function ObeyaModeToggleSection(_props: ObeyaModeToggleSectionProps) {
  const { subMode, setSubMode } = useObeyaExplorer();
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data } = useObeyaProjects(listStrategy!, {
    limit: 50,
    ...(lang ? { lang } : {}),
  });
  const L = data?.labels ?? {};

  return (
    <div className="flex items-center gap-1 px-2 py-2 border-b border-border bg-muted/30">
      <ToggleButton
        active={subMode === 'projects'}
        onClick={() => setSubMode('projects')}
        icon={FolderKanban}
        label={L.projects ?? 'Projects'}
      />
      <ToggleButton
        active={subMode === 'simulations'}
        onClick={() => setSubMode('simulations')}
        icon={Bot}
        label={L.simulations ?? 'Simulations'}
      />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors flex-1 justify-center',
        active
          ? 'bg-background text-foreground shadow-sm border border-border'
          : 'text-muted-foreground hover:bg-accent/50',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export type { ObeyaSubMode };
